import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Store, Megaphone, CheckCircle2, Loader2, Star, Zap, Check } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const adTypes = [
  { value: 'business', label: 'Business Listing', icon: Store, description: 'List your Sikh-owned business, restaurant, or service' },
  { value: 'announcement', label: 'Community Announcement', icon: Megaphone, description: 'Share community news, events, or announcements' },
];

const categories = [
  { value: 'restaurant', label: 'Restaurant / Food' },
  { value: 'store', label: 'Retail Store' },
  { value: 'service', label: 'Professional Service' },
  { value: 'gurudwara', label: 'Gurudwara' },
  { value: 'other', label: 'Other' },
];

const pricingPlans = [
  {
    id: 'free',
    name: 'Basic Listing',
    price: 'Free',
    priceValue: 0,
    description: 'Get listed in our directory',
    features: [
      'Basic directory listing',
      'Business name & description',
      'Contact information',
      'Requires approval (1-2 days)'
    ],
    highlight: false
  },
  {
    id: 'featured',
    name: 'Featured Listing',
    price: '$25',
    priceValue: 25,
    period: '/month',
    description: 'Stand out from the crowd',
    features: [
      'Everything in Basic',
      'Featured badge & gold border',
      'Priority placement',
      'Instant activation',
      'Homepage visibility'
    ],
    highlight: true
  },
  {
    id: 'sponsored',
    name: 'Sponsored Banner',
    price: '$50',
    priceValue: 50,
    period: '/month',
    description: 'Maximum visibility',
    features: [
      'Everything in Featured',
      'Banner ad on homepage',
      'Top of directory listing',
      'Social media promotion',
      'Analytics dashboard'
    ],
    highlight: false
  }
];

const AdvertisePage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [form, setForm] = useState({
    ad_type: 'business',
    title: '',
    description: '',
    business_name: '',
    category: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    website: '',
    image_url: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedPlan === 'free') {
        // Free listing - submit for approval
        await axios.post(`${API_URL}/advertisements/submit`, form);
        setSubmitted(true);
        toast.success('Listing submitted for review!');
      } else {
        // Paid listing - redirect to Stripe checkout
        const response = await axios.post(`${API_URL}/advertisements/checkout`, {
          listing_type: selectedPlan,
          ad_data: form,
          origin_url: window.location.origin
        });
        
        // Redirect to Stripe
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit listing');
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream-100 py-16" data-testid="advertise-success">
        <div className="max-w-lg mx-auto px-4">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Listing Submitted!
              </h2>
              <p className="text-gray-600 mb-6">
                Your free listing has been submitted for review. We'll notify you once it's approved (usually within 1-2 days).
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Want faster approval? Upgrade to a Featured Listing for instant activation!<br />
                Contact us at: <a href="mailto:Khalsasidak13@gmail.com" className="text-saffron-600">Khalsasidak13@gmail.com</a>
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => window.location.href = '/directory'} className="btn-outline">
                  View Directory
                </Button>
                <Button
                  onClick={() => { setSubmitted(false); setForm({ ad_type: 'business', title: '', description: '', business_name: '', category: '', contact_name: '', contact_email: '', contact_phone: '', address: '', website: '', image_url: '' }); }}
                  className="btn-primary"
                >
                  Submit Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="advertise-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Advertise With Us
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            List your business and connect with the global Sikh community.
          </p>
        </div>

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {pricingPlans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative cursor-pointer transition-all ${
                selectedPlan === plan.id
                  ? 'ring-2 ring-saffron-500 border-saffron-500'
                  : 'hover:border-saffron-200'
              } ${plan.highlight ? 'border-gold-400 shadow-lg' : ''}`}
              onClick={() => setSelectedPlan(plan.id)}
              data-testid={`plan-${plan.id}`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gold-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                    plan.id === 'free' ? 'bg-gray-100' :
                    plan.id === 'featured' ? 'bg-gold-100' : 'bg-royal-100'
                  }`}>
                    {plan.id === 'free' && <Store className="w-6 h-6 text-gray-600" />}
                    {plan.id === 'featured' && <Star className="w-6 h-6 text-gold-600" />}
                    {plan.id === 'sponsored' && <Zap className="w-6 h-6 text-royal-600" />}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-gray-900">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-saffron-600">{plan.price}</span>
                    {plan.period && <span className="text-gray-500">{plan.period}</span>}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                </div>
                
                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button
                  className={`w-full mt-6 ${
                    selectedPlan === plan.id 
                      ? 'btn-primary' 
                      : 'btn-outline'
                  }`}
                  onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan.id); }}
                >
                  {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payment Info for Manual Payments */}
        <Card className="mb-8 bg-royal-50 border-royal-200">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold text-royal-800 mb-2">
              💳 Payment Options
            </h3>
            <p className="text-royal-700 text-sm">
              <strong>Online:</strong> Pay securely with credit/debit card via Stripe (Featured & Sponsored plans)<br />
              <strong>Manual:</strong> Contact us for Zelle, PayPal, or Venmo payments at <a href="mailto:Khalsasidak13@gmail.com" className="underline">Khalsasidak13@gmail.com</a> or call <a href="tel:240-474-7537" className="underline">240-474-7537</a>
            </p>
          </CardContent>
        </Card>

        {/* Ad Type Selection */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {adTypes.map((type) => (
            <Card
              key={type.value}
              className={`cursor-pointer transition-all ${
                form.ad_type === type.value
                  ? 'ring-2 ring-saffron-500 border-saffron-500'
                  : 'hover:border-saffron-200'
              }`}
              onClick={() => setForm({ ...form, ad_type: type.value })}
              data-testid={`ad-type-${type.value}`}
            >
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  form.ad_type === type.value ? 'bg-saffron-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <type.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{type.label}</h3>
                  <p className="text-sm text-gray-500">{type.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Listing Information</CardTitle>
            <CardDescription>Fill in your details to submit your listing</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {form.ad_type === 'business' && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      value={form.business_name}
                      onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                      placeholder="Your business name"
                      required
                      data-testid="ad-business-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                      <SelectTrigger data-testid="ad-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="title">
                  {form.ad_type === 'business' ? 'Tagline / Short Title *' : 'Announcement Title *'}
                </Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={form.ad_type === 'business' ? 'e.g., Authentic Punjabi Cuisine' : 'Announcement title'}
                  required
                  data-testid="ad-title"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe your business or announcement..."
                  rows={4}
                  required
                  data-testid="ad-description"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Your name"
                    required
                    data-testid="ad-contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Contact Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    data-testid="ad-contact-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_phone">Phone Number</Label>
                  <Input
                    id="contact_phone"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    placeholder="240-xxx-xxxx"
                    data-testid="ad-contact-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="https://..."
                    data-testid="ad-website"
                  />
                </div>
              </div>

              {form.ad_type === 'business' && (
                <div>
                  <Label htmlFor="address">Business Address</Label>
                  <Input
                    id="address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Street, City, State, ZIP"
                    data-testid="ad-address"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="image_url">Image URL (Logo or Photo)</Label>
                <Input
                  id="image_url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="ad-image-url"
                />
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-medium text-gray-900">Selected Plan: {pricingPlans.find(p => p.id === selectedPlan)?.name}</p>
                    <p className="text-saffron-600 font-bold">
                      {selectedPlan === 'free' ? 'Free' : `$${pricingPlans.find(p => p.id === selectedPlan)?.priceValue}/month`}
                    </p>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="ad-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : selectedPlan === 'free' ? (
                    'Submit for Review'
                  ) : (
                    `Pay $${pricingPlans.find(p => p.id === selectedPlan)?.priceValue} & Submit`
                  )}
                </Button>
              </div>

              <p className="text-center text-sm text-gray-500">
                {selectedPlan === 'free' 
                  ? 'Free listings are reviewed before being published (1-2 days).'
                  : 'Paid listings are activated instantly after payment.'}
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdvertisePage;
